/**
 * Adaptive music as a truth table (07 §4): which layers sound, given the garrison alert, the
 * mission phase, the clock and the Osprey. The sequencer reads this at every bar line, so
 * changes quantise to the bar. Pure.
 */

import type { MissionPhase, OspreyPhase } from '../../sim/types';
import type { LayerId } from './pattern';

export interface ScoreState {
  phase: MissionPhase;
  alert: number;
  /** Inside the last 60 seconds of the mission clock. */
  urgent: boolean;
  osprey: OspreyPhase;
  objectiveComplete: boolean;
  /** The mission has a result: the debrief cue owns the music now. */
  ended: boolean;
  /** The "1987 score" option: no in-mission layers at all. */
  score1987: boolean;
}

export type LayerMask = Record<LayerId, boolean>;

export const createLayerMask = (): LayerMask => ({
  always: true, roll: false, drone: false, motif: false, hat: false, bass: false, drums: false, lead: false,
  urgency: false, extraction: false, lift: false,
});

export function activeLayers(s: ScoreState, out: LayerMask): LayerMask {
  const on = !s.score1987 && !s.ended;
  const ground = on && s.phase === 'ground';
  const pickup = ground && (s.osprey === 'inbound' || s.osprey === 'hovering');
  out.always = true;
  // Flyover: the snare roll builds toward the buzzer. Under the canopy: nothing but wind.
  out.roll = on && s.phase === 'flyover';
  out.drone = ground;
  // Motif fragments belong to the quiet: once the drums are in, or the theme is reprising, they would only clutter.
  out.motif = ground && s.alert < 3 && !pickup;
  out.hat = ground && s.alert >= 1;
  out.bass = ground && s.alert >= 2;
  out.drums = ground && s.alert >= 3;
  out.lead = ground && s.alert >= 3 && !pickup;
  out.urgency = ground && s.urgent;
  out.extraction = pickup && !s.objectiveComplete;
  out.lift = pickup && s.objectiveComplete;
  return out;
}
